import * as React from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';

import {
  formatMemberProfileChartDate,
  getMemberProfileChartCutoffDateKey,
  toMemberProfileChartDateKey,
} from '../../../../../utils';

export const CHART_PRIMARY_COLOR = '#FF00FF';

const roundToOneDecimal = (value) => {
  const num = Number(value) || 0;
  return Math.round(num * 10) / 10;
};

const option = (dates, values) => ({
  grid: {
    height: 405,
    left: 40,
    top: 10,
    right: 0,
  },
  xAxis: {
    type: 'category',
    // boundaryGap: false,
    // axisTick: {
    //   show: false,
    // },
    // axisLabel: {
    //   align: 'right',
    // },
    data: dates,
    show: false,
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      formatter: function (value) {
        return (Math.round(value * 10) / 10).toFixed(1);
      },
    },
    // min: 100,
    // max: 150,
  },
  tooltip: {
    trigger: 'axis',
    formatter: function (params) {
      if (!params || !params.length) return '';
      const param = params[0];
      const date = formatMemberProfileChartDate(param.axisValue, 'YYYY-MM-DD');
      const value = typeof param.value === 'number' 
        ? (Math.round(param.value * 10) / 10).toFixed(1)
        : param.value;
      return `${date}<br/>${value} Lbs.`;
    },
    axisPointer: {
      type: 'line',
      label: {
        formatter: function (params) {
          return formatMemberProfileChartDate(params.value, 'YYYY-MM-DD');
        },
      },
    },
  },
  series: [
    {
      data: values,
      type: 'line',
      smooth: true,
      showSymbol: false,
      symbolSize: 12,
      lineStyle: {
        width: 3,
        color: CHART_PRIMARY_COLOR,
      },
      itemStyle: {
        color: CHART_PRIMARY_COLOR,
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: CHART_PRIMARY_COLOR,
          },
          {
            offset: 1,
            color: 'rgba(229, 139, 204, 0.00)',
          },
        ]),
      },
    },
  ],
});

const PERIODS = Object.freeze({
  allTime: 'All-Time',
  last7Days: 'Last 7 Days',
  last30Days: 'Last 30 Days',
  last90Days: 'Last 90 Days',
  last365Days: 'Last 365 Days',
});

const PERIOD_OPTIONS = Object.freeze([
  PERIODS.allTime,
  PERIODS.last7Days,
  PERIODS.last30Days,
  PERIODS.last90Days,
  PERIODS.last365Days,
]);

const PERIOD_LOOKBACK_DAYS = Object.freeze({
  [PERIODS.last7Days]: 7,
  [PERIODS.last30Days]: 30,
  [PERIODS.last90Days]: 90,
  [PERIODS.last365Days]: 365,
});

const getFilteredDates = (allDates, selectedPeriod) => {
  if (selectedPeriod === PERIODS.allTime) return allDates;

  const lookbackDays = PERIOD_LOOKBACK_DAYS[selectedPeriod];
  if (!lookbackDays) return allDates;

  const cutoffDateKey = getMemberProfileChartCutoffDateKey(lookbackDays);
  if (!cutoffDateKey) return allDates;

  return allDates.filter((dt) => {
    const dateKey = toMemberProfileChartDateKey(dt);
    return typeof dateKey === 'string' && dateKey >= cutoffDateKey;
  });
};

const ChartSection = ({ title, data }) => {
  const [ anchorEl, setAnchorEl ] = React.useState(null);
  const [ selectedPeriod, setSelectedPeriod ] = React.useState(PERIODS.allTime);
  const open = Boolean(anchorEl);
  const allDates = Object.keys(data);
  allDates.sort();
  const dates = getFilteredDates(allDates, selectedPeriod);

  const values = dates.map((date) => roundToOneDecimal(data[date]));

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newPeriod) => {
    setSelectedPeriod(newPeriod);
    setAnchorEl(null);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", tl: "center" }}
        mb={{ xs: 5, tl: 9 }}
        flexDirection={{ xs: "column", tl: "row" }}
      >
        <Typography fontWeight={700}>{title} - One Rep Max (Lbs.) Progress</Typography>
        <Box
          sx={{
            borderRadius: "8px",
            backgroundColor: "rgb(238, 238, 238)",
            color: "rgb(33, 43, 54)",
            height: "36px",
            position: "relative",
            paddingLeft: "10px",
            paddingRight: "30px",
          }}
          mt={{ xs: 1, tl: 0 }}
          onClick={handleOpenMenu}
        >
          <Typography
            sx={{ cursor: "pointer" }}
            fontSize={14}
            lineHeight="36px"
            display="flex"
            alignItems="center"
          >
            {selectedPeriod}
            <CalendarTodayIcon sx={{ marginLeft: 1, fontSize: 14 }} />
          </Typography>
          <Box
            sx={{
              position: "absolute",
              right: "0px",
              top: "6px",
            }}
          >
            <KeyboardArrowDownIcon />
          </Box>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {},
          }}
        >
          {PERIOD_OPTIONS.map((option) => (
            <MenuItem key={option} selected={option === selectedPeriod} onClick={() => handleSelect(option)}>
              {option}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: 446 }}>
        <ReactEcharts option={option(dates, values)} style={{ width: '100%', height: '100%' }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: 'calc(100% - 40px)',
            pl: 5,
            mt: 1,

            '& p': {
              color: '#A2A3A5',
              fontSize: 13,
            },
          }}
        >
          <Typography>
            {dates.length > 0 ? formatMemberProfileChartDate(dates[0], 'MMM YYYY') : ''}
          </Typography>
          <Typography>
            {dates.length > 0 ? formatMemberProfileChartDate(dates[dates.length - 1], 'MMM YYYY') : ''}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChartSection;
